import CloseRoundedIcon from '@mui/icons-material/CloseRounded';
import LocationOnOutlinedIcon from '@mui/icons-material/LocationOnOutlined';
import Box from '@mui/material/Box';
import IconButton from '@mui/material/IconButton';
import { alpha, type SxProps, type Theme } from '@mui/material/styles';
import type { HTMLAttributes } from 'react';
import { useTranslation } from 'react-i18next';
import type {
  AddressSuggestion,
  PlacesSearchOptions,
} from 'react-places-autocomplete';
import PlacesAutocomplete from 'react-places-autocomplete';

import type { SuggestionLabel } from './addressSearchFieldTypes';

interface AddressSearchFieldProps {
  value: string;
  onChange: (address: string) => void;
  onSelect: (address: string) => void;
  placeholder?: string;
  disabled?: boolean;
  searchOptions?: PlacesSearchOptions;
}

const suggestionLabel = (suggestion: AddressSuggestion): SuggestionLabel => {
  const formatted = suggestion.formattedSuggestion;
  if (formatted?.mainText) {
    return {
      main: formatted.mainText,
      sub: formatted.secondaryText || '',
    };
  }
  return { main: suggestion.description, sub: '' };
};

const focusRing = (theme: Theme): string =>
  `0 0 0 3px ${alpha(theme.palette.text.primary, 0.08)}`;

const fieldSx = (isOpen: boolean): SxProps<Theme> => ({
  display: 'flex',
  minHeight: 48,
  alignItems: 'center',
  gap: 1.25,
  borderRadius: isOpen ? '12px 12px 0 0' : '12px',
  border: 1,
  borderColor: isOpen ? 'text.secondary' : 'divider',
  bgcolor: isOpen ? 'rgb(var(--line))' : 'rgb(var(--field))',
  boxShadow: isOpen ? focusRing : 'none',
  py: 0,
  pl: 1.75,
  pr: 1.5,
  transition: 'border-color 150ms, box-shadow 150ms, background-color 150ms',
  '&:focus-within': {
    borderColor: 'text.secondary',
    bgcolor: 'rgb(var(--line))',
    boxShadow: focusRing,
  },
});

const suggestionSx = (active: boolean): SxProps<Theme> => ({
  display: 'flex',
  width: '100%',
  cursor: 'pointer',
  alignItems: 'flex-start',
  gap: 1.25,
  border: 0,
  borderTop: 1,
  borderColor: 'divider',
  bgcolor: active ? 'rgb(var(--line))' : 'transparent',
  px: 1.75,
  py: 1.25,
  textAlign: 'left',
  color: active ? 'text.primary' : 'text.secondary',
  '&:hover': {
    bgcolor: 'rgb(var(--line))',
    color: 'text.primary',
  },
  '&:hover .MuiSvgIcon-root': { color: 'primary.main' },
});

const AddressSearchField = ({
  value,
  onChange,
  onSelect,
  placeholder = 'Start typing an address…',
  disabled = false,
  searchOptions,
}: AddressSearchFieldProps): JSX.Element => {
  const { t } = useTranslation();

  return (
  <PlacesAutocomplete
    value={value}
    onChange={onChange}
    onSelect={onSelect}
    searchOptions={searchOptions}
  >
    {({ getInputProps, suggestions, getSuggestionItemProps, loading }) => {
      const isOpen = !disabled && (loading || suggestions.length > 0);

      return (
        <Box sx={{ position: 'relative', width: '100%' }}>
          <Box sx={fieldSx(isOpen)}>
            <LocationOnOutlinedIcon
              sx={{ flexShrink: 0, fontSize: 20, color: 'text.secondary' }}
            />
            <Box
              component="input"
              {...getInputProps({ placeholder, disabled })}
              sx={{
                minWidth: 0,
                flex: 1,
                border: 0,
                bgcolor: 'transparent',
                p: 0,
                font: 'inherit',
                color: 'text.primary',
                boxShadow: 'none',
                height: 46,
                fontSize: 14,
                outline: 'none',
                '&::placeholder': { color: 'text.secondary', opacity: 1 },
                '&:disabled': {
                  cursor: 'not-allowed',
                  color: 'text.secondary',
                },
              }}
            />
            {value && !disabled ? (
              <IconButton
                aria-label={t('map.clearAddress')}
                onClick={() => onChange('')}
                sx={{
                  height: 28,
                  width: 28,
                  flexShrink: 0,
                  bgcolor: 'rgb(var(--line))',
                  color: 'text.secondary',
                  '&:hover': {
                    bgcolor: 'rgb(var(--line))',
                    color: 'text.primary',
                  },
                }}
              >
                <CloseRoundedIcon sx={{ fontSize: 16 }} />
              </IconButton>
            ) : null}
          </Box>
          {isOpen ? (
            <Box
              role="listbox"
              sx={{
                position: 'absolute',
                left: 0,
                right: 0,
                top: '100%',
                zIndex: 50,
                mt: '-1px',
                maxHeight: 260,
                overflowY: 'auto',
                borderRadius: '0 0 12px 12px',
                border: 1,
                borderTop: 0,
                borderColor: 'text.secondary',
                bgcolor: 'rgb(var(--field))',
                boxShadow: (theme) =>
                  theme.palette.mode === 'dark'
                    ? '0 16px 32px rgba(0,0,0,0.4)'
                    : '0 12px 28px rgba(0,0,0,0.12)',
              }}
            >
              {loading ? (
                <Box
                  sx={{
                    py: 1.5,
                    pl: '44px',
                    pr: 2,
                    fontSize: 13,
                    color: 'text.secondary',
                  }}
                >
                  Searching addresses…
                </Box>
              ) : null}
              {suggestions.map((suggestion) => {
                const { main, sub } = suggestionLabel(suggestion);
                const { key: suggestionKey, ...suggestionProps } =
                  getSuggestionItemProps(
                    suggestion
                  ) as HTMLAttributes<HTMLDivElement> & {
                    key?: string;
                  };
                return (
                  <Box
                    key={
                      suggestionKey ??
                      suggestion.placeId ??
                      suggestion.description
                    }
                    {...suggestionProps}
                    sx={suggestionSx(Boolean(suggestion.active))}
                  >
                    <LocationOnOutlinedIcon
                      sx={{
                        mt: '1px',
                        flexShrink: 0,
                        fontSize: 18,
                        color: suggestion.active
                          ? 'primary.main'
                          : 'text.secondary',
                      }}
                    />
                    <Box
                      component="span"
                      sx={{
                        display: 'flex',
                        minWidth: 0,
                        flexDirection: 'column',
                        gap: 0.5,
                      }}
                    >
                      <Box
                        component="span"
                        sx={{
                          fontSize: 13,
                          fontWeight: 600,
                          lineHeight: 1.3,
                          color: 'inherit',
                        }}
                      >
                        {main}
                      </Box>
                      {sub ? (
                        <Box
                          component="span"
                          sx={{
                            fontSize: 12,
                            lineHeight: 1.3,
                            color: 'text.secondary',
                          }}
                        >
                          {sub}
                        </Box>
                      ) : null}
                    </Box>
                  </Box>
                );
              })}
            </Box>
          ) : null}
        </Box>
      );
    }}
  </PlacesAutocomplete>
  );
};

export default AddressSearchField;
